'use client';

import { useState } from 'react';
import { BookOpen, Sparkles, Star, Trash2 } from 'lucide-react';
import { ImageBackdropLayers, cn } from '@/components/help-center/latest-power-shared';
import { MaskedSvgIcon } from '@/components/help-center/MaskedSvgIcon';
import { PortalCrystalSeparator } from '@/components/help-center/PortalCrystalSeparator';
import { PowerSectionSmileyIcon } from '@/components/help-center/powers/power-gallery-view';
import { PainelDashedAddCardButton } from '@/components/painel/cms/SmilieCodeField';
import { RichTextEditor } from '@/components/painel/cms/RichTextEditor';
import {
  CuriosityIconPickerModal,
} from '@/components/painel/powers/CuriosityIconPickerModal';
import { LoreHeroSmileyModal } from '@/components/painel/powers/LoreHeroSmileyModal';
import { painelSelectContentClass, painelSwitchClass } from '@/components/painel/painel-styles';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { isLoreCuriosityColorfulIcon, LORE_CURIOSITY_ICON_TINT } from '@/lib/lore-curiosity-icons';
import { isLibraryIconRef, libraryIconSlug } from '@/lib/icon-library';
import { Icon } from '@/components/icons/Icon';
import {
  emptyPowerLoreChapter,
  emptyPowerLoreCuriosity,
  getPowerLoreTheme,
  POWER_LORE_CHAPTER_LABELS,
  POWER_LORE_THEME_IDS,
  powerLoreHasRenderableContent,
  seedPowerLoreForEditor,
  type PowerLore,
  type PowerLoreThemeId,
} from '@/lib/power-lore';
import PowerDetailLoreSection from '@/components/help-center/powers/PowerDetailLoreSection';

type PowerLoreEditorProps = {
  lore: PowerLore;
  onChange: (next: PowerLore) => void;
  powerName: string;
  powerSlug: string;
  defaultSmiley?: string;
  /** Smilies do power — atalhos no modal do smiley principal. */
  suggestedSmilies?: string[];
  /** Se false, só pré-visualização (não Super Admin). */
  canEdit?: boolean;
};

function isLocalIconRef(value: string): boolean {
  const v = value.trim();
  if (isLibraryIconRef(v)) return true;
  return v.startsWith('/') || v.startsWith('icons/') || /\.svg$/i.test(v);
}

function resolveLocalIconSrc(value: string): string {
  const v = value.trim();
  if (v.startsWith('/')) return v;
  if (v.startsWith('icons/')) return `/${v}`;
  if (/\.svg$/i.test(v)) return `/icons/${v.replace(/^\/+/, '')}`;
  return v;
}

function LoreEditorLibraryIcon({
  iconRef,
  tintColor,
}: {
  iconRef: string;
  tintColor: string;
}) {
  const src = resolveLocalIconSrc(iconRef);
  if (isLoreCuriosityColorfulIcon(src)) {
    return <img src={src} alt="" className="h-7 w-7 object-contain" draggable={false} />;
  }
  return (
    <span style={{ color: tintColor }} className="inline-flex">
      <MaskedSvgIcon src={src} className="h-7 w-7" />
    </span>
  );
}

const ghostInput =
  'border-0 bg-transparent px-1.5 py-0.5 shadow-none transition-[background-color,box-shadow,color] duration-200';

const editableTitleHover =
  'rounded-md hover:bg-white/[0.06] focus-visible:bg-white/[0.06] focus-visible:ring-1 focus-visible:ring-sky-400/30';

const enterAnim = 'animate-in fade-in slide-in-from-bottom-3 duration-500 fill-mode-both';

function themeAddAccent(themeId: string): 'sky' | 'emerald' | 'amber' {
  if (themeId === 'rare') return 'emerald';
  if (themeId === 'epic') return 'amber';
  return 'sky';
}

const LORE_THEME_TOOLTIP: Record<
  PowerLoreThemeId,
  { title: string; description: string }
> = {
  normal: {
    title: 'História Normal',
    description: 'Tom azul clássico — narrativa padrão do power.',
  },
  rare: {
    title: 'História Rara',
    description: 'Tom verde — história especial ou pouco comum.',
  },
  epic: {
    title: 'História Épica',
    description: 'Tom âmbar — história lendária e de alto impacto.',
  },
};

const loreThemeTooltipClass =
  'z-[80] max-w-[220px] border border-white/10 bg-[#0a1468] px-3 py-2 font-poppins text-xs text-blue-50 shadow-lg shadow-black/30';

export function PowerLoreEditor({
  lore,
  onChange,
  powerName,
  powerSlug,
  defaultSmiley = '',
  suggestedSmilies = [],
  canEdit = true,
}: PowerLoreEditorProps) {
  const [iconPickerId, setIconPickerId] = useState<string | null>(null);
  const [heroSmileyOpen, setHeroSmileyOpen] = useState(false);

  const patch = (partial: Partial<PowerLore>) => onChange({ ...lore, ...partial });

  const setEnabled = (enabled: boolean) => {
    if (
      enabled &&
      lore.chapters.length === 0 &&
      lore.curiosities.length === 0 &&
      !lore.title.trim() &&
      !lore.subtitle.trim() &&
      !lore.intro.trim()
    ) {
      onChange(
        seedPowerLoreForEditor({
          powerName,
          powerSlug,
          defaultSmiley: lore.smiley || defaultSmiley,
          theme: lore.theme || 'normal',
        })
      );
      return;
    }
    patch({ enabled });
  };

  const patchChapter = (id: string, partial: Partial<(typeof lore.chapters)[number]>) => {
    patch({
      chapters: lore.chapters.map((c) => (c.id === id ? { ...c, ...partial } : c)),
    });
  };

  const patchCuriosity = (id: string, partial: Partial<(typeof lore.curiosities)[number]>) => {
    patch({
      curiosities: lore.curiosities.map((c) => (c.id === id ? { ...c, ...partial } : c)),
    });
  };

  const theme = getPowerLoreTheme(lore.theme);
  const themeId = lore.theme || 'normal';
  const smileyCode =
    (lore.smiley || defaultSmiley || powerSlug || 'smile').replace(/[()]/g, '').trim() || 'smile';

  const previewPower = {
    name: powerName,
    slug: powerSlug,
    smilies: [smileyCode],
  };

  const iconTarget = lore.curiosities.find((c) => c.id === iconPickerId);

  if (!canEdit) {
    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-amber-400/20 bg-amber-500/[0.06] px-4 py-3">
          <p className="font-poppins text-sm font-semibold text-amber-100/90">
            Apenas Super Admins podem editar a Lore
          </p>
          <p className="mt-1 font-inter text-xs text-amber-100/55">
            Pode ver a pré-visualização abaixo, mas alterações nesta secção estão bloqueadas.
          </p>
        </div>
        {lore.enabled && powerLoreHasRenderableContent(lore) ? (
          <div className="overflow-hidden rounded-2xl border border-white/10">
            <PowerDetailLoreSection power={previewPower} lore={lore} compact />
          </div>
        ) : (
          <p className="font-inter text-sm text-slate-500">
            Esta Lore ainda não está publicada ou não tem conteúdo.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
        <Switch
          id="lore-enabled"
          checked={lore.enabled}
          onCheckedChange={setEnabled}
          className={painelSwitchClass}
        />
        <Label htmlFor="lore-enabled" className="font-poppins text-slate-300">
          Publicar secção de Lore / História
        </Label>
      </div>

      {!lore.enabled ? (
        <p className="font-inter text-sm text-slate-500">
          Desligada por padrão. Ative para editar a história directamente no preview (subtítulo,
          título, capítulos e curiosidades).
        </p>
      ) : (
        <div className="rounded-2xl border border-white/10">
          <section
            className="relative overflow-visible border-y border-blue-900/50 py-10"
            aria-label="Editor visual da Lore"
          >
            <div className="pointer-events-none absolute inset-0 bg-[#020018]" aria-hidden />
            <ImageBackdropLayers className="pointer-events-none absolute inset-0 opacity-80" />
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#000b6a]/50 via-[#030023]/70 to-[#020018]"
              aria-hidden
            />
            <div
              className={cn(
                'pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent to-transparent',
                theme.topHairline
              )}
              aria-hidden
            />

            <div className="relative z-10 mx-auto max-w-4xl px-6">
              <div
                className={cn(
                  'relative overflow-visible rounded-3xl border bg-[#030023]/55 p-6 backdrop-blur-sm sm:p-10 lg:p-12',
                  'ring-1',
                  theme.cardBorder,
                  theme.cardRing,
                  theme.cardShadow
                )}
              >
                <div className="absolute right-3 top-3 z-20 flex flex-col items-end gap-1.5 sm:right-5 sm:top-5">
                  <p className="font-poppins text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400/90">
                    Modo de história
                  </p>
                  <TooltipProvider delayDuration={200}>
                    <div className="flex items-center gap-1.5" role="group" aria-label="Modo de história">
                      {POWER_LORE_THEME_IDS.map((id) => {
                        const t = getPowerLoreTheme(id);
                        const active = themeId === id;
                        const tip = LORE_THEME_TOOLTIP[id];
                        return (
                          <Tooltip key={id}>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                aria-label={tip.title}
                                aria-pressed={active}
                                onClick={() => patch({ theme: id as PowerLoreThemeId })}
                                className={cn(
                                  'h-5 w-5 rounded-full ring-2 transition-[box-shadow,opacity] sm:h-6 sm:w-6',
                                  active
                                    ? 'ring-white ring-offset-1 ring-offset-[#030023]'
                                    : 'ring-transparent opacity-80 hover:opacity-100'
                                )}
                                style={{ backgroundColor: t.swatch }}
                              />
                            </TooltipTrigger>
                            <TooltipContent
                              side="bottom"
                              align="center"
                              collisionPadding={12}
                              className={loreThemeTooltipClass}
                            >
                              <p className="font-semibold text-white">{tip.title}</p>
                              <p className="mt-0.5 font-inter text-[11px] leading-snug text-blue-100/70">
                                {tip.description}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </TooltipProvider>
                </div>

                <div
                  className={cn(
                    'pointer-events-none absolute -left-20 top-10 h-56 w-56 rounded-full blur-3xl',
                    theme.orbPrimary
                  )}
                  aria-hidden
                />
                <div
                  className={cn(
                    'pointer-events-none absolute -right-16 bottom-0 h-48 w-48 rounded-full blur-3xl',
                    theme.orbSecondary
                  )}
                  aria-hidden
                />

                <header className="relative mb-10 flex flex-col items-center text-center sm:mb-12">
                  <div className="relative mb-5 flex w-full justify-center">
                    <div className="relative flex items-center justify-center">
                      <div
                        className={cn(
                          'pointer-events-none absolute inset-0 scale-150 rounded-full blur-2xl',
                          theme.smileyGlow
                        )}
                        aria-hidden
                      />
                      <button
                        type="button"
                        title="Editar smiley em destaque"
                        aria-label="Editar smiley em destaque"
                        className={cn(
                          'relative mx-auto flex items-center justify-center rounded-full border transition',
                          'hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50',
                          theme.smileyFrame,
                          'h-[5.25rem] w-[5.25rem] p-2 sm:h-24 sm:w-24 sm:p-2.5'
                        )}
                        onClick={() => setHeroSmileyOpen(true)}
                      >
                        <span className="flex h-full w-full items-center justify-center">
                          <PowerSectionSmileyIcon code={smileyCode} size="hero" />
                        </span>
                      </button>
                    </div>
                  </div>

                  <Input
                    value={lore.subtitle}
                    onChange={(e) => patch({ subtitle: e.target.value })}
                    placeholder="Nos arquivos do xat"
                    className={cn(
                      ghostInput,
                      editableTitleHover,
                      'mb-2 h-auto text-center font-poppins text-[11px] font-semibold uppercase tracking-[0.28em]',
                      'placeholder:text-white/25'
                    )}
                    style={{ color: theme.swatch }}
                  />
                  <Input
                    value={lore.title}
                    onChange={(e) => patch({ title: e.target.value })}
                    placeholder="A história por trás do Power"
                    className={cn(
                      ghostInput,
                      editableTitleHover,
                      'h-auto max-w-2xl text-center font-poppins text-2xl font-bold tracking-tight text-white',
                      'placeholder:text-white/25 sm:text-3xl lg:text-[2rem]'
                    )}
                  />
                </header>

                <div className="relative mx-auto max-w-2xl overflow-visible">
                  {lore.chapters.length > 0 ? (
                    <div
                      className={cn(
                        'absolute bottom-2 left-[11px] top-2 w-px -translate-x-1/2 bg-gradient-to-b sm:left-[15px]',
                        theme.timeline
                      )}
                      aria-hidden
                    />
                  ) : null}

                  <ol className="relative list-none space-y-0 overflow-visible p-0">
                    {lore.chapters.map((chapter, index) => (
                      <li
                        key={chapter.id}
                        className={cn('relative overflow-visible pl-10 sm:pl-12', enterAnim)}
                      >
                        <span
                          className="absolute left-[11px] top-2 z-10 flex h-3.5 w-3.5 -translate-x-1/2 items-center justify-center sm:left-[15px]"
                          aria-hidden
                        >
                          <span className={cn('absolute h-3.5 w-3.5 rounded-full', theme.dot)} />
                          <span
                            className={cn('absolute h-7 w-7 animate-pulse rounded-full', theme.dotHalo)}
                          />
                        </span>

                        <article className="overflow-visible pb-12 last:pb-4">
                          <div className="mb-3 flex flex-wrap items-center gap-2">
                            <span
                              className={cn(
                                'font-mono text-[10px] font-semibold tracking-[0.2em]',
                                theme.chapterMeta
                              )}
                            >
                              CAPÍTULO {index + 1}
                            </span>
                            <Select
                              value={
                                POWER_LORE_CHAPTER_LABELS.some((opt) => opt.id === chapter.label)
                                  ? chapter.label || '__none__'
                                  : '__none__'
                              }
                              onValueChange={(v) =>
                                patchChapter(chapter.id, {
                                  label: v === '__none__' ? '' : v,
                                })
                              }
                            >
                              <SelectTrigger
                                className={cn(
                                  'h-7 w-auto min-w-[7.5rem] gap-1 rounded-md border px-2 py-0.5',
                                  'font-poppins text-[10px] font-semibold uppercase tracking-[0.14em]',
                                  'shadow-none focus:ring-1 focus:ring-offset-0',
                                  theme.badge,
                                  '[&>svg]:h-3 [&>svg]:w-3 [&>svg]:opacity-70'
                                )}
                              >
                                <SelectValue placeholder="Sem label" />
                              </SelectTrigger>
                              <SelectContent className={painelSelectContentClass}>
                                {POWER_LORE_CHAPTER_LABELS.map((opt) => (
                                  <SelectItem
                                    key={opt.id || '__none__'}
                                    value={opt.id || '__none__'}
                                  >
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="ml-auto h-7 px-2 text-rose-300/80 hover:bg-rose-500/10 hover:text-rose-200"
                              onClick={() =>
                                patch({
                                  chapters: lore.chapters.filter((c) => c.id !== chapter.id),
                                })
                              }
                              aria-label="Remover capítulo"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>

                          <Input
                            value={chapter.title}
                            onChange={(e) => patchChapter(chapter.id, { title: e.target.value })}
                            placeholder="Título do capítulo"
                            className={cn(
                              ghostInput,
                              editableTitleHover,
                              'mb-2 h-auto font-poppins text-lg font-bold tracking-tight text-white',
                              'placeholder:text-white/25 sm:text-xl'
                            )}
                          />

                          <RichTextEditor
                            value={chapter.body}
                            onChange={(body) => patchChapter(chapter.id, { body })}
                            floatingToolbar
                            toolbar="extended"
                            editorMinHeightClass="min-h-[4.5rem]"
                            resetKey={chapter.id}
                            className="mt-1"
                          />
                        </article>
                      </li>
                    ))}
                  </ol>

                  <div className="mt-2 pl-10 sm:pl-12">
                    <PainelDashedAddCardButton
                      label="Adicionar capítulo"
                      accent={themeAddAccent(themeId)}
                      size="compact"
                      icon={<BookOpen className="h-4 w-4" aria-hidden />}
                      onClick={() =>
                        patch({
                          chapters: [
                            ...lore.chapters,
                            emptyPowerLoreChapter({
                              label: 'HISTÓRIA',
                              title: 'Novo capítulo',
                              body: '<p>Escreva o conteúdo deste capítulo…</p>',
                            }),
                          ],
                        })
                      }
                    />
                  </div>
                </div>

                <div className="mx-auto mt-10 max-w-2xl overflow-visible">
                  {lore.chapters.length > 0 || lore.curiosities.length > 0 ? (
                    <PortalCrystalSeparator className="mb-8" accentRgb={theme.accentRgb} />
                  ) : null}

                  <div className="grid gap-4 overflow-visible">
                    {lore.curiosities.map((item) => {
                      const iconRef = item.smiley.trim();
                      const localIcon = iconRef && isLocalIconRef(iconRef);
                      return (
                        <div
                          key={item.id}
                          className={cn(
                            enterAnim,
                            'relative overflow-visible rounded-2xl border border-amber-400/25',
                            'bg-gradient-to-br from-amber-400/[0.12] via-[#030023]/40 to-sky-500/[0.08]',
                            'p-4 shadow-[0_0_24px_-8px_rgba(251,191,36,0.35)] ring-1 ring-amber-300/10 sm:p-5'
                          )}
                        >
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-3 top-3 z-10 h-7 px-2 text-rose-300/80 hover:bg-rose-500/10 hover:text-rose-200"
                            onClick={() =>
                              patch({
                                curiosities: lore.curiosities.filter((c) => c.id !== item.id),
                              })
                            }
                            aria-label="Remover curiosidade"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>

                          <div className="relative flex gap-3 pr-10">
                            <button
                              type="button"
                              title="Escolher ícone"
                              onClick={() => setIconPickerId(item.id)}
                              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-amber-400/30 bg-amber-400/15 transition hover:border-amber-300/50 hover:bg-amber-400/25"
                            >
                              {localIcon ? (
                                isLibraryIconRef(iconRef) && libraryIconSlug(iconRef) ? (
                                  <Icon
                                    name={libraryIconSlug(iconRef)!}
                                    className="h-7 w-7 text-amber-200"
                                  />
                                ) : (
                                  <LoreEditorLibraryIcon
                                    iconRef={iconRef}
                                    tintColor={LORE_CURIOSITY_ICON_TINT}
                                  />
                                )
                              ) : iconRef ? (
                                <PowerSectionSmileyIcon code={iconRef} size="md" />
                              ) : (
                                <Sparkles className="h-5 w-5 text-amber-300" aria-hidden />
                              )}
                            </button>
                            <div className="min-w-0 flex-1 overflow-visible">
                              <Input
                                value={item.title}
                                onChange={(e) =>
                                  patchCuriosity(item.id, { title: e.target.value })
                                }
                                placeholder="Você sabia?"
                                className={cn(
                                  ghostInput,
                                  editableTitleHover,
                                  'mb-1.5 h-auto font-poppins text-[11px] font-bold uppercase tracking-[0.18em] text-amber-200/90',
                                  'placeholder:text-amber-200/35'
                                )}
                              />
                              <RichTextEditor
                                value={item.body}
                                onChange={(body) => patchCuriosity(item.id, { body })}
                                floatingToolbar
                                toolbar="extended"
                                editorMinHeightClass="min-h-[3.5rem]"
                                resetKey={item.id}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    <PainelDashedAddCardButton
                      label="Adicionar curiosidade"
                      accent="amber"
                      size="compact"
                      icon={<Star className="h-4 w-4 fill-current" aria-hidden />}
                      onClick={() =>
                        patch({
                          curiosities: [
                            ...lore.curiosities,
                            emptyPowerLoreCuriosity({
                              title: 'Você sabia?',
                              body: '<p>Escreva um detalhe curioso sobre este power…</p>',
                            }),
                          ],
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      )}

      <LoreHeroSmileyModal
        open={heroSmileyOpen}
        onOpenChange={setHeroSmileyOpen}
        value={smileyCode}
        powerName={powerName}
        suggestions={[
          ...suggestedSmilies,
          defaultSmiley,
          powerSlug,
          smileyCode,
        ]}
        onConfirm={(next) => {
          patch({ smiley: next.replace(/[()]/g, '').trim() });
        }}
      />

      <CuriosityIconPickerModal
        open={iconPickerId != null}
        onOpenChange={(open) => {
          if (!open) setIconPickerId(null);
        }}
        value={iconTarget?.smiley ?? ''}
        onConfirm={(next) => {
          if (!iconPickerId) return;
          patchCuriosity(iconPickerId, { smiley: next });
          setIconPickerId(null);
        }}
      />
    </div>
  );
}
